import React, { useCallback, useEffect, useMemo } from "react";
import * as Contacts from "expo-contacts";
import { PhoneContact } from "./phone_contact";
import { SelectChooser, SelectItemType } from "../select/SelectChooser";
import Button, { ButtonMode, ButtonSize } from "../Button";
import { useI18n } from "../../hooks/i18n-hooks";
import { Linking, Platform, StyleSheet } from "react-native";
import { useTheme } from "../../hooks/use-theme";
import Text from "../Text";

export type AddressChooserProps = {
    onSelect?: (phoneContact: PhoneContact) => void;
    disabled?: boolean;
    buttonMode?: ButtonMode;
    buttonSize?: ButtonSize;
    buttonLabel?: string;
};

export const PhoneContactChooser: React.FC<AddressChooserProps> = ({
    onSelect,
    disabled,
    buttonMode,
    buttonSize,
    buttonLabel,
}) => {
    const styles = useStyles();
    const [isVisible, setIsVisible] = React.useState(false);
    const [enabled, setEnabled] = React.useState(false);
    const [denied, setDenied] = React.useState(false);
    const [granted, setGranted] = React.useState(false);

    const goToAppSettings = useCallback(async () => {
        if (Platform.OS === "ios") {
            await Linking.openURL("app-settings:contacts");
        } else {
            await Linking.openSettings();
        }
    }, []);

    const i18n = useI18n();
    useEffect(() => {
        const _func = async () => {
            const perms = await Contacts.getPermissionsAsync();
            const status: Contacts.PermissionStatus = perms.status;
            setDenied(status === Contacts.PermissionStatus.DENIED);
            setGranted(status === Contacts.PermissionStatus.GRANTED);
            if (
                status === Contacts.PermissionStatus.UNDETERMINED ||
                status === Contacts.PermissionStatus.GRANTED
            ) {
                setEnabled(true);
            }
        };
        void _func();
    }, []);

    const doCheckPermission = useCallback(async () => {
        const perms = await Contacts.getPermissionsAsync();
        let status: Contacts.PermissionStatus = perms.status;
        if (status === Contacts.PermissionStatus.UNDETERMINED) {
            const permsAsk = await Contacts.requestPermissionsAsync();
            status = permsAsk.status;
        }
        setEnabled(status === Contacts.PermissionStatus.GRANTED);
        setGranted(status === Contacts.PermissionStatus.GRANTED);
        setDenied(status === Contacts.PermissionStatus.DENIED);
        return status === Contacts.PermissionStatus.GRANTED;
    }, []);

    const openChooser = useCallback(async () => {
        if (await doCheckPermission()) {
            setIsVisible(true);
        }
    }, [doCheckPermission]);

    const searchContact = React.useCallback(
        async (filter: string) => {
            if (!enabled || !granted) {
                return [];
            }
            const { data } = await Contacts.getContactsAsync({
                name: filter || undefined,
                fields: [
                    Contacts.Fields.PhoneNumbers,
                    Contacts.Fields.FirstName,
                    Contacts.Fields.LastName,
                    Contacts.Fields.Company,
                ],
            });
            if (data.length > 0) {
                return data.reduce((acc, contact) => {
                    contact.phoneNumbers?.forEach((phoneNumber) => {
                        if (phoneNumber.number) {
                            acc.push({
                                value: {
                                    firstName: contact.firstName,
                                    lastName: contact.lastName,
                                    phoneNumber: phoneNumber.number,
                                },
                                label: [contact.firstName, contact.lastName, contact.company].join(
                                    " ",
                                ),
                                tags: [phoneNumber.number],
                            });
                        }
                    });
                    return acc;
                }, [] as SelectItemType<PhoneContact>[]);
            }
            return [];
        },
        [enabled, granted],
    );
    return (
        <>
            {denied ? (
                <Text
                    style={styles.errorText}
                    onPress={() => {
                        void goToAppSettings();
                    }}
                >
                    {i18n.t("phone_contact.permission_denied")}
                </Text>
            ) : (
                <Button
                    label={buttonLabel || i18n.t("phone_contact.search")}
                    onPress={() => openChooser()}
                    processing={isVisible}
                    disabled={!enabled || isVisible || disabled}
                    buttonMode={buttonMode}
                    size={buttonSize}
                />
            )}
            <SelectChooser<PhoneContact>
                visible={isVisible}
                onDismiss={() => {
                    setIsVisible(false);
                }}
                searchItems={searchContact}
                onSelect={(i) => {
                    setIsVisible(false);
                    onSelect?.(i.value);
                }}
                placeholder={buttonLabel || i18n.t("phone_contact.search")}
            />
        </>
    );
};

const useStyles = () => {
    const { colors, fonts } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                errorText: {
                    ...fonts.bodySmall,
                    color: colors.onWarningBackground,
                    width: "100%",
                    marginHorizontal: 4,
                },
            }),
        [fonts.bodySmall, colors.onWarningBackground],
    );
};
